import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, Zap, CheckCircle2, ArrowRight, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const SampleBrandPage = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section with Brand Colors */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Circuit Background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-yellow-400/10 blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-yellow-400/10 blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-4xl mx-auto text-center">
            {/* Logo-inspired Circle */}
            <div className="mb-12 flex justify-center">
              <div className="relative w-40 h-40">
                <div className="absolute inset-0 rounded-full border-4 border-yellow-400 shadow-[0_0_30px_rgba(255,215,0,0.5)] animate-spin-slow"></div>
                <div className="absolute inset-2 rounded-full border-4 border-cyan-400 shadow-[0_0_30px_rgba(0,255,255,0.5)] animate-spin-reverse"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Zap className="w-16 h-16 text-white" />
                </div>
              </div>
            </div>

            {/* Hero Content */}
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
              JETCO
              <span className="block text-yellow-400">
                ELECTRICAL CONTRACTORS
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              Powering Brooklyn with Professional Electrical Solutions
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button 
                size="lg" 
                className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold shadow-[0_0_20px_rgba(255,215,0,0.5)]"
              >
                Get Free Estimate
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-black shadow-[0_0_20px_rgba(0,255,255,0.3)]"
              >
                <Phone className="w-5 h-5 mr-2" />
                (667) 455-6368
              </Button>
            </div>

            {/* Feature Pills */}
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center space-x-2 bg-yellow-400/20 border border-yellow-400/50 rounded-full px-6 py-3">
                <CheckCircle2 className="w-5 h-5 text-yellow-400" />
                <span className="text-white font-medium">24/7 Emergency Service</span>
              </div>
              <div className="flex items-center space-x-2 bg-gradient-to-r from-cyan-400/20 to-transparent border border-cyan-400/50 rounded-full px-6 py-3">
                <Star className="w-5 h-5 text-cyan-400" />
                <span className="text-white font-medium">15+ Years Experience</span>
              </div>
            </div>
          </div>
        </div>

        {/* Cityscape Bottom Border */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-yellow-400/5">
          <svg className="w-full h-full" viewBox="0 0 1200 100" preserveAspectRatio="none">
            <path 
              d="M0,100 L0,50 L50,50 L50,30 L100,30 L100,50 L150,50 L150,20 L200,20 L200,50 L250,50 L250,40 L300,40 L300,50 L350,50 L350,25 L400,25 L400,50 L450,50 L450,35 L500,35 L500,50 L550,50 L550,30 L600,30 L600,50 L650,50 L650,20 L700,20 L700,50 L750,50 L750,40 L800,40 L800,50 L850,50 L850,25 L900,25 L900,50 L950,50 L950,35 L1000,35 L1000,50 L1050,50 L1050,30 L1100,30 L1100,50 L1150,50 L1150,40 L1200,40 L1200,100 Z"
              fill="url(#cityGradient)"
              opacity="0.3"
            />
            <defs>
              <linearGradient id="cityGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#FFD700" />
                <stop offset="50%" stopColor="#00FFFF" />
                <stop offset="100%" stopColor="#FFD700" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Our <span className="text-yellow-400">Services</span>
            </h2>
            <div className="w-24 h-1 bg-yellow-400 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { title: 'EV Charger Installation', icon: Zap },
              { title: 'Panel Upgrades', icon: CheckCircle2 },
              { title: 'Smart Home Systems', icon: Star }
            ].map((service) => {
              const IconComponent = service.icon;
              return (
                <Card 
                  key={service.title} 
                  className="bg-gray-900 border-2 border-gray-800 hover:border-yellow-400 transition-all duration-300 group"
                >
                  <CardHeader>
                    <div className="w-16 h-16 rounded-full bg-yellow-400 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-[0_0_20px_rgba(255,215,0,0.3)]">
                      <IconComponent className="w-8 h-8 text-black" />
                    </div>
                    <CardTitle className="text-2xl text-white">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-400">Professional installation and maintenance services for all your electrical needs.</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Banner */}
      <section className="py-16 bg-yellow-400">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-4">Ready to Power Your Project?</h2>
          <p className="text-xl text-gray-900 mb-8">Get your free estimate today</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-black text-white hover:bg-gray-900">
              <Phone className="w-5 h-5 mr-2" />
              Call (667) 455-6368
            </Button>
            <Button size="lg" variant="outline" className="border-2 border-black text-black hover:bg-black hover:text-white">
              <Mail className="w-5 h-5 mr-2" />
              Email Us
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-gray-800 py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400 mb-4">&copy; 2025 Jetco Electrical Contractors LLC. All rights reserved.</p>
          <div className="flex justify-center space-x-6">
            <a href="tel:(667)455-6368" className="text-yellow-400 hover:text-cyan-400 transition-colors">
              (667) 455-6368
            </a>
            <span className="text-gray-600">|</span>
            <a href="mailto:Jetcoelectricllc@gmail.com" className="text-cyan-400 hover:text-yellow-400 transition-colors">
              Jetcoelectricllc@gmail.com
            </a>
          </div>
        </div>
      </footer>

      <style jsx>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes spin-reverse {
          from { transform: rotate(360deg); }
          to { transform: rotate(0deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
        .animate-spin-reverse {
          animation: spin-reverse 12s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default SampleBrandPage;
